import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Crown, Home, ArrowLeft } from "lucide-react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminMobileDropdown from "../../components/AdminMobileDropdown";
import AdminContent from "../../components/AdminContent";
import AdminNotificationCenter from "../../components/AdminNotificationCenter";

export default function CourseSystemAdmin() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersThisMonth: 0,
    totalCourses: 0,
    activeCourses: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    growthRate: 0,
    revenueThisMonth: 0,
    revenueLastMonth: 0,
  });
  const [users, setUsers] = useState<any[]>([]);

  useEffect(() => {
    // Check if admin is authenticated via AdminPasswordGuard
    const adminAuthData = sessionStorage.getItem('admin_authenticated');
    if (adminAuthData) {
      try {
        const { timestamp } = JSON.parse(adminAuthData);
        const now = Date.now();
        const SESSION_DURATION = 3600000; // 1 hour in milliseconds
        
        // Check if admin session is still valid
        if (now - timestamp < SESSION_DURATION) {
          loadAdminData();
          return;
        }
      } catch (e) {
        // Invalid session data, continue to check Firebase user
      }
    }

    // Check if Firebase user is admin (fallback)
    if (user?.email !== "viitamint@gmail.com" && user?.role !== "admin") {
      navigate("/");
      return;
    }

    loadAdminData();
  }, [user, navigate]);

  const loadAdminData = async () => {
    try {
      // Load all users
      const usersSnap = await getDocs(collection(db, "users"));
      const usersData = usersSnap.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);

      // Load all courses
      const coursesSnap = await getDocs(collection(db, "courses"));
      const activeCourses = coursesSnap.docs.filter(doc => doc.data().published).length;

      // Load all payments (course purchases) - only count instructor-approved payments
      const paymentsSnap = await getDocs(collection(db, "payments"));
      const courseRevenue = paymentsSnap.docs
        .filter(doc => doc.data().status === 'completed' && doc.data().approvedBy)
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      // Load all subscription payments (instructor subscriptions) - only count admin-approved payments
      const subscriptionPaymentsSnap = await getDocs(collection(db, "subscription_payments"));
      const subscriptionRevenue = subscriptionPaymentsSnap.docs
        .filter(doc => doc.data().status === 'approved')
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      // Calculate total revenue
      const totalRevenue = courseRevenue + subscriptionRevenue;

      // Calculate revenue this month and last month
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

      const revenueThisMonth = [
        ...paymentsSnap.docs.filter(doc => doc.data().status === 'completed' && doc.data().approvedBy),
        ...subscriptionPaymentsSnap.docs.filter(doc => doc.data().status === 'approved')
      ]
        .filter(doc => {
          const data = doc.data();
          const createdAt = data.createdAt?.toDate();
          return createdAt && createdAt >= thisMonthStart;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      const revenueLastMonth = [
        ...paymentsSnap.docs.filter(doc => doc.data().status === 'completed' && doc.data().approvedBy),
        ...subscriptionPaymentsSnap.docs.filter(doc => doc.data().status === 'approved')
      ]
        .filter(doc => {
          const data = doc.data();
          const createdAt = data.createdAt?.toDate();
          return createdAt && createdAt >= lastMonthStart && createdAt <= lastMonthEnd;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      // Calculate growth rate
      const growthRate = revenueLastMonth > 0 
        ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100 
        : 0;

      // Count new users this month
      const newUsersThisMonth = usersData.filter(u => {
        const createdAt = u.createdAt?.toDate();
        return createdAt && createdAt >= thisMonthStart;
      }).length;

      // Count active subscriptions
      const subscriptionsSnap = await getDocs(collection(db, "subscriptions"));
      const activeSubscriptions = subscriptionsSnap.docs.filter(doc => 
        doc.data().status === 'active' || doc.data().status === 'grace_period'
      ).length;

      setStats({
        totalUsers: usersData.length,
        newUsersThisMonth,
        totalCourses: coursesSnap.size,
        activeCourses,
        activeSubscriptions,
        totalRevenue,
        growthRate,
        revenueThisMonth,
        revenueLastMonth,
      });

      setLoading(false);
    } catch (error) {
      console.error("Error loading admin data:", error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Course System Admin</h1>
                  <p className="text-xs text-gray-500">ระบบสร้างคอร์สเรียน</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <AdminNotificationCenter />
              <Link
                to="/"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">หน้าหลัก</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <div className="hidden lg:block">
          <AdminSidebar 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            systemType="course"
          />
        </div>

        {/* Mobile Dropdown */}
        <div className="lg:hidden w-full">
          <AdminMobileDropdown 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            systemType="course"
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-8">
          <AdminContent 
            activeSection={activeSection}
            stats={stats}
            users={users}
            loading={loading}
            systemType="course"
            onSectionChange={setActiveSection}
          />
        </div>
      </div>
    </div>
  );
}
